import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxMenu } from '@/components/molecules/CheckboxMenu';
import { type FeedingPenOption, penDisplayNames } from '@/domain/feedingDashboard';

/** 'all' = every pen; an array = explicit subset (may be empty while picking). */
export type PenSelection = 'all' | number[];

interface PenFilterSelectProps {
    /** Every pen of the period, whatever is selected. */
    options: FeedingPenOption[];
    selection: PenSelection;
    onChange: (selection: PenSelection) => void;
}

const ALL_GROUP_ID = 'all-pens';

/**
 * Multi-select pen filter: "All pens" plus a checkbox per pen (IPS req 5).
 *
 * Lists the pens of the period, so a pen can be historical or have no Bioscope at all
 * (planning-system data only, statement 6). Both are marked: the first explains why the
 * pen is gone from the site, the second why its Bioscope columns stay blank.
 */
export function PenFilterSelect(props: PenFilterSelectProps) {
    const { options, selection, onChange } = props;
    const { t } = useTranslation();
    const names = useMemo(() => penDisplayNames(options), [options]);

    const allSelected = selection === 'all';
    const selectedIds = allSelected ? options.map((option) => option.cageId) : selection;

    // "All pens" is the group row over every pen, so it ticks, clears and shows a partial
    // selection the way the other filters' groups do.
    const sections = [
        {
            group: { id: ALL_GROUP_ID, label: t('feedingModule.filter.allPens') },
            options: options.map((option) => {
                const notes = [
                    option.historicalSince !== null ? t('feedingModule.filter.historical') : null,
                    option.bioscopeInPeriod ? null : t('feedingModule.filter.noBioscope'),
                ].filter((note): note is string => note !== null);
                return {
                    value: String(option.cageId),
                    label: names.get(option.cageId) ?? option.cageName,
                    description: notes.length > 0 ? notes.join(', ') : undefined,
                };
            }),
        },
    ];

    // An empty selection shows nothing, so the trigger must not claim every pen.
    const summary = allSelected
        ? t('feedingModule.filter.allPens')
        : selection.length === 0
          ? t('feedingModule.filter.none')
          : selection.length === 1
            ? (names.get(selection[0]) ?? '1')
            : t('feedingModule.filter.selectedCount', { count: selection.length });

    return (
        <CheckboxMenu
            label={t('feedingModule.filter.label')}
            hideLabel
            summary={summary}
            options={sections}
            selected={selectedIds.map(String)}
            onChange={(values) => {
                const ids = options
                    .map((option) => option.cageId)
                    .filter((id) => values.includes(String(id)));
                // Every pen picked one by one is the same as "all pens".
                onChange(ids.length === options.length ? 'all' : ids);
            }}
        />
    );
}
